/* HDN · Pase de diapositivas de la portada (sections/hdn-slideshow.liquid), sobre Horizon.
 *
 * Es un elemento propio: se pone en marcha al aparecer en la página y otra vez si
 * el editor de temas vuelve a pintar la sección, y lo limpia todo al salir.
 *
 * - Desliza en horizontal, como el tema anterior: 500 ms con la curva «ease» de CSS y en
 *   bucle (de la última a la primera sigue en la misma dirección, sin rebobinar). Solo
 *   transform, con GSAP (assets/hdn-gsap.min.js); sin GSAP o con menos movimiento, el
 *   cambio es inmediato. La foto que entra mide lo mismo que la primera (sin zoom), así el
 *   navegador no la cuenta como un LCP nuevo.
 * - Pasa sola cada N segundos, salvo que el visitante prefiera menos movimiento.
 *   Se para al pasar el ratón, al enfocar dentro, con el botón de pausa, con la
 *   pestaña oculta, fuera de pantalla y mientras se edita en el editor de temas.
 */
(function () {
  if (customElements.get('hdn-slideshow')) return;

  var DURACION = 0.5;

  /* Curva «ease» de CSS, cubic-bezier(0.25, 0.1, 0.25, 1), la del pase del tema anterior. */
  function curvaCss(p1x, p1y, p2x, p2y) {
    function x(t) { return ((1 - 3 * p2x + 3 * p1x) * t + (3 * p2x - 6 * p1x)) * t * t + 3 * p1x * t; }
    function y(t) { return ((1 - 3 * p2y + 3 * p1y) * t + (3 * p2y - 6 * p1y)) * t * t + 3 * p1y * t; }
    function dx(t) { return 3 * (1 - 3 * p2x + 3 * p1x) * t * t + 2 * (3 * p2x - 6 * p1x) * t + 3 * p1x; }
    return function (avance) {
      if (avance <= 0 || avance >= 1) return avance;
      var t = avance;
      for (var i = 0; i < 6; i++) {
        var d = dx(t);
        if (Math.abs(d) < 1e-6) break;
        t -= (x(t) - avance) / d;
      }
      return y(Math.min(1, Math.max(0, t)));
    };
  }
  var EASE = curvaCss(0.25, 0.1, 0.25, 1);

  class HdnSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-slide]'));
      if (this.slides.length < 2) return;

      this.live = this.querySelector('[data-slides]');
      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-dot]'));
      this.toggleBtn = this.querySelector('[data-toggle]');
      this.index = 0;
      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplay = this.dataset.autoplay === 'true' && !this.reduce.matches;
      this.delay = Math.max(3, parseFloat(this.dataset.speed) || 5) * 1000;
      this.paused = false;
      this.holds = new Set();
      this.abort = new AbortController();

      var opts = { signal: this.abort.signal };
      var self = this;

      var prev = this.querySelector('[data-prev]');
      var next = this.querySelector('[data-next]');
      if (prev) prev.addEventListener('click', function () { self.go(self.index - 1, false, -1); }, opts);
      if (next) next.addEventListener('click', function () { self.go(self.index + 1, false, 1); }, opts);
      this.dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { self.go(i); }, opts);
      });

      if (this.toggleBtn) {
        if (!this.autoplay) this.toggleBtn.hidden = true;
        this.toggleBtn.addEventListener('click', function () { self.setPaused(!self.paused); }, opts);
      }

      this.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); self.go(self.index - 1, false, -1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); self.go(self.index + 1, false, 1); }
      }, opts);

      /* Pausas temporales: se reanuda sola al desaparecer el motivo. */
      this.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') self.holdOn('hover'); }, opts);
      this.addEventListener('pointerleave', function () { self.holdOff('hover'); }, opts);
      this.addEventListener('focusin', function () { self.holdOn('focus'); }, opts);
      this.addEventListener('focusout', function (e) { if (!self.contains(e.relatedTarget)) self.holdOff('focus'); }, opts);
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) self.holdOn('hidden'); else self.holdOff('hidden');
      }, opts);
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(function (entries) {
          if (entries[0].isIntersecting) self.holdOff('offscreen'); else self.holdOn('offscreen');
        });
        this.io.observe(this);
      }

      /* Deslizar con el dedo (touch-action: pan-y deja el gesto horizontal para nosotros). */
      var x0 = null;
      var y0 = null;
      this.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse') return;
        x0 = e.clientX;
        y0 = e.clientY;
      }, opts);
      this.addEventListener('pointerup', function (e) {
        if (x0 === null) return;
        var dx = e.clientX - x0;
        var dy = e.clientY - y0;
        x0 = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) self.go(self.index + (dx < 0 ? 1 : -1), false, dx < 0 ? 1 : -1);
      }, opts);
      this.addEventListener('pointercancel', function () { x0 = null; }, opts);

      /* Editor de temas: al elegir un bloque, se enseña su diapositiva y se para. */
      document.addEventListener('shopify:block:select', function (e) {
        var i = self.slides.indexOf(e.target);
        if (i === -1) return;
        self.holdOn('editor');
        self.go(i, true);
      }, opts);
      document.addEventListener('shopify:block:deselect', function () { self.holdOff('editor'); }, opts);

      this.schedule();
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      if (this.io) this.io.disconnect();
      clearTimeout(this.timer);
      if (window.gsap && this.slides) window.gsap.killTweensOf(this.slides);
    }

    go(to, instant, sentido) {
      var n = this.slides.length;
      var target = ((to % n) + n) % n;
      if (target === this.index) return;
      /* Sentido: el de la flecha, el dedo o el paso automático; con los puntos, hacia el elegido. */
      var dir = sentido || (target > this.index ? 1 : -1);
      var from = this.slides[this.index];
      var into = this.slides[target];
      this.index = target;

      from.setAttribute('aria-hidden', 'true');
      from.inert = true;
      into.removeAttribute('aria-hidden');
      into.inert = false;
      this.dots.forEach(function (dot, i) { dot.setAttribute('aria-current', i === target ? 'true' : 'false'); });

      this.swap(from, into, !!instant, dir);
      this.schedule();
    }

    swap(from, into, instant, dir) {
      var gsap = window.gsap;
      var slides = this.slides;

      /* Coloca una diapositiva en su sitio sin animar (con GSAP, para que sepa dónde está). */
      function colocar(el, visibilidad) {
        if (gsap) gsap.set(el, { xPercent: 0, visibility: visibilidad });
        else { el.style.transform = ''; el.style.visibility = visibilidad; }
      }

      /* Si se pulsa a mitad de un cambio, se corta el anterior y se parte de donde iba. */
      if (gsap) gsap.killTweensOf(slides);
      slides.forEach(function (s) {
        if (s === from || s === into) return;
        colocar(s, 'hidden');
        s.style.zIndex = '';
      });
      from.style.zIndex = '1';
      into.style.zIndex = '2';

      if (!gsap || instant || this.reduce.matches) {
        colocar(into, 'visible');
        colocar(from, 'hidden');
        return;
      }

      gsap.set(from, { visibility: 'visible' });
      gsap.set(into, { visibility: 'visible', xPercent: 100 * dir });
      gsap.to(from, { xPercent: -100 * dir, duration: DURACION, ease: EASE });
      gsap.to(into, {
        xPercent: 0,
        duration: DURACION,
        ease: EASE,
        onComplete: function () {
          gsap.set(from, { visibility: 'hidden', xPercent: 0 });
        }
      });
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.autoplay || this.paused || this.holds.size) return;
      var self = this;
      this.timer = setTimeout(function () { self.go(self.index + 1, false, 1); }, this.delay);
    }

    holdOn(reason) {
      this.holds.add(reason);
      clearTimeout(this.timer);
    }

    holdOff(reason) {
      this.holds.delete(reason);
      this.schedule();
    }

    setPaused(paused) {
      this.paused = paused;
      if (this.toggleBtn) {
        this.toggleBtn.dataset.state = paused ? 'paused' : 'playing';
        this.toggleBtn.setAttribute('aria-label', paused ? this.toggleBtn.dataset.labelPlay : this.toggleBtn.dataset.labelPause);
      }
      /* Con el pase parado, los cambios se anuncian; mientras pasa solo, no (patrón WAI de carrusel). */
      if (this.live) this.live.setAttribute('aria-live', paused ? 'polite' : 'off');
      this.schedule();
    }
  }

  customElements.define('hdn-slideshow', HdnSlideshow);
})();
